import { useLayoutEffect, useState } from "react";

// Read-only copy of the live Offices grid profile. Its other tracks determine
// when the first and third minmax tracks grow; using only their maxima does not
// reproduce the reference's responsive behavior. Do not share/edit Offices code.
const OFFICES_REFERENCE_TRACKS = "minmax(190px,224px) 56px minmax(120px,160px) 120px 76px 96px 100px 88px 94px 80px 88px 104px minmax(0,1fr) 64px";
const ACTION_WIDTH = 64;
const INITIAL_WIDTHS = [190, 56, 120, 120, 180, 104, 72, 60, 116, 44, ACTION_WIDTH];
const INITIAL_GEOMETRY = {
  widths: INITIAL_WIDTHS,
  tableWidth: INITIAL_WIDTHS.reduce((sum, width) => sum + width, 0),
};

function textWidth(element) {
  if (!element) return 0;
  const range = document.createRange();
  range.selectNodeContents(element);
  return range.getBoundingClientRect?.().width ?? 0;
}

function headerContentWidth(header, index) {
  const title = header.querySelector(`[data-testid^="leases-list-header-group-"] > span`);
  if (!title) return 0;
  // Measure the text node alone: the Sq Ft / $/SF caret is absolutely positioned
  // to retain the existing label centering and needs space on BOTH sides.
  const range = document.createRange();
  range.selectNodeContents(title);
  if (title.firstChild?.nodeType === Node.TEXT_NODE) range.selectNode(title.firstChild);
  const caret = header.querySelector("svg");
  const caretWidth = caret ? caret.getBoundingClientRect().width + 1 : 0;
  return (range.getBoundingClientRect?.().width ?? 0)
    + caretWidth * (index === 7 || index === 9 ? 2 : 1);
}

function trailingContentWidths(row) {
  const cells = row.cells;
  const date = cells[4].querySelector('[data-testid="expire-date-value"]');
  const countdown = cells[4].querySelector('[data-testid="expire-countdown"]');
  const separator = countdown?.previousElementSibling;
  const separatorStyle = separator ? getComputedStyle(separator) : null;
  const expiry = textWidth(date) + textWidth(countdown) + textWidth(separator)
    + (separatorStyle ? parseFloat(separatorStyle.marginLeft) + parseFloat(separatorStyle.marginRight) : 0);
  const arrangement = Math.max(
    textWidth(cells[5].querySelector('[data-testid="lease-type-value"]')),
    ...Array.from(cells[5].querySelectorAll('[data-testid^="sublease-head-"]'), textWidth),
  );
  return [expiry, arrangement, ...Array.from(cells).slice(6, 10).map(textWidth)];
}

function measureGeometry(shell, header, body) {
  const available = body.parentElement.clientWidth;
  if (!available || !document.createRange().getBoundingClientRect) return null;

  // Let the browser resolve the reference minmax tracks at the actual card width.
  // The zero-height, clipped probe cannot contribute off-page scroll or hit targets.
  const probe = document.createElement("div");
  const grid = document.createElement("div");
  Object.assign(probe.style, { position: "absolute", height: "0", width: "0", overflow: "hidden", visibility: "hidden" });
  Object.assign(grid.style, { display: "grid", width: `${Math.max(980, available)}px`, gridTemplateColumns: OFFICES_REFERENCE_TRACKS });
  probe.append(grid);
  shell.append(probe);
  let firstFour;
  try {
    firstFour = getComputedStyle(grid).gridTemplateColumns.split(" ").slice(0, 4).map(parseFloat);
  } finally {
    probe.remove();
  }

  const minima = Array.from(header.querySelectorAll("th")).slice(4, 10)
    .map((cell, index) => headerContentWidth(cell, index + 4));
  for (const row of body.querySelectorAll('tr[data-testid^="leases-list-row-"]')) {
    trailingContentWidths(row).forEach((width, index) => {
      minima[index] = Math.max(minima[index], width);
    });
  }
  // One pixel each side retains the existing typography without wasting the
  // content budget. Round up to keep subpixel glyph edges inside their cells.
  const trailing = minima.map((width) => Math.ceil(width) + 2);
  const visible = trailing.map((_width, index) =>
    !(shell.clientWidth <= 900 && index >= 3)
    && !(shell.clientWidth <= 650 && (index === 1 || index === 2)));
  const minimum = firstFour.reduce((sum, width) => sum + width, ACTION_WIDTH)
    + trailing.reduce((sum, width, index) => sum + (visible[index] ? width : 0), 0);
  const tableWidth = Math.max(available, minimum);
  const extra = (tableWidth - minimum) / visible.filter(Boolean).length;
  const widths = [...firstFour, ...trailing.map((width, index) => visible[index] ? width + extra : 0), ACTION_WIDTH];
  return { widths, tableWidth };
}

// Only the active v2 boundary opts in. No lease values are changed or cached;
// native glyph measurements keep dates, supplied terms and rent fully visible.
export function useLeasesListGeometry({ enabled, shellRef, headerRef, bodyRef, rows, density }) {
  const [geometry, setGeometry] = useState(INITIAL_GEOMETRY);

  useLayoutEffect(() => {
    if (!enabled) return undefined;
    const shell = shellRef.current;
    const header = headerRef.current?.querySelector("table");
    const body = bodyRef.current?.querySelector("table");
    if (!shell || !header || !body) return undefined;
    let disposed = false;
    const measure = () => {
      if (disposed) return;
      const next = measureGeometry(shell, header, body);
      if (!next) return;
      setGeometry((previous) => previous.tableWidth === next.tableWidth
        && previous.widths.every((width, index) => width === next.widths[index]) ? previous : next);
    };
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(shell);
    window.addEventListener("resize", measure);
    document.fonts?.ready.then(measure);
    return () => {
      disposed = true;
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [enabled, shellRef, headerRef, bodyRef, rows, density]);

  return geometry;
}
